import type {
  FaceRow,
  FaceSection,
  InputDefinition,
  NodeFace,
  SummaryContext,
  UniversalNodeSchema,
} from './universal-node-schema'

type NodeCategory = UniversalNodeSchema['category']

/**
 * Node widths in px, mirrored by `--node-width` in backtest-reactflow.css. A
 * closed subnet card takes the wide one (collapse-model.ts).
 */
export const NODE_WIDTHS = { standard: 280, medium: 305, wide: 330 } as const

/**
 * Each category's face. A node overrides only what differs (`schema.face`),
 * so a change here reaches every node of the category at once. See
 * docs/superpowers/specs/2026-10-04-backtest-node-rendering-audit.md.
 */
export const CATEGORY_FACES: Record<NodeCategory, NodeFace> = {
  data: { width: 'standard', inputLabels: 'name', outputs: 'per-output', inputControl: 'field' },
  // The full-width source dropdown on every indicator input (Chris,
  // 2026-10-06), in place of a pill and a separate line picker.
  indicators: {
    width: 'standard',
    inputLabels: 'none',
    outputs: 'single-handle',
    inputControl: 'source',
  },
  logic: { width: 'standard', inputLabels: 'letter', outputs: 'per-output', inputControl: 'field' },
  // Math, utilities and filters read like the Condition (Chris, 2026-10-06):
  // each input named on its own line above the full-width source.
  // An unwired math input says what to connect rather than a red "No input".
  math: {
    width: 'standard',
    inputLabels: 'letter',
    outputs: 'per-output',
    inputControl: 'source',
    emptyInputs: 'prompt',
  },
  'entry-exit-logic': {
    width: 'standard',
    inputLabels: 'name',
    outputs: 'per-output',
    inputControl: 'field',
  },
  // An unwired utility input says "Connect a source…" too (Chris, 2026-10-06).
  utilities: {
    width: 'standard',
    inputLabels: 'letter',
    outputs: 'per-output',
    inputControl: 'source',
    emptyInputs: 'prompt',
  },
  debug: { width: 'standard', inputLabels: 'none', outputs: 'per-output', inputControl: 'field' },
  filter: { width: 'standard', inputLabels: 'name', outputs: 'per-output', inputControl: 'source' },
  presets: { width: 'standard', inputLabels: 'name', outputs: 'per-output', inputControl: 'field' },
}

/** The node's face: its category's, with the node's own overrides on top. */
export function resolveFace(schema: UniversalNodeSchema): NodeFace {
  return { ...CATEGORY_FACES[schema.category], ...schema.face }
}

/** The right side of an input's row: its own `control`, else the face's. */
export function inputControl(face: NodeFace, input: InputDefinition): NodeFace['inputControl'] {
  return input.control ?? face.inputControl
}

export interface SourceRowLabel {
  text: string
  /** 'gutter': a letter on the row, left of the field; 'stacked': a name above it. */
  placement: 'gutter' | 'stacked'
}

const LETTER = /^[A-H]$/

/**
 * What names a full-width source row (Chris, 2026-10-06: "just the letters is
 * easier"). A lettered input is its letter, in a gutter on the row, so a math
 * node reads A, B, then the equation, and a Condition A, Operator, B. A real
 * name ("Range low") or the face's own label (the Crossover's "Input A") sits
 * above its field. A lone input needs no name, the header says what the node
 * is, unless an equation reads its letter (√A). None where the face draws no
 * input labels.
 */
export function sourceRowLabel(
  face: NodeFace,
  input: InputDefinition,
  index: number,
  inputCount: number,
  lettersInEquation = false,
): SourceRowLabel | null {
  if (face.inputLabels === 'none' || input.hideLabel) return null
  if (input.faceLabel) return { text: input.faceLabel, placement: 'stacked' }
  const name = input.displayName ?? ''
  const lettered = name === '' || LETTER.test(name)
  if (inputCount === 1 && !(lettered && lettersInEquation)) return null
  if (!lettered) return { text: name, placement: 'stacked' }
  return { text: name || String.fromCharCode(65 + index), placement: 'gutter' }
}

/**
 * What an unwired input shows: red (`missing`) while the node needs it, "the
 * red dashed line" saying something needs doing (Chris, 2026-10-06), and the
 * words in it. A prompting face (math) says what to connect, the input's own
 * ("Connect the exponent") or "Connect a source…"; otherwise the input's own
 * words, or none, which a source control reads as "No input" when required.
 */
export function emptyInputState(
  face: NodeFace,
  input: InputDefinition,
  required: boolean,
): { missing: boolean; text: string | undefined } {
  const text =
    face.emptyInputs === 'prompt' ? (input.emptyText ?? 'Connect a source…') : input.emptyText
  return { missing: required, text }
}

/**
 * The inputs whose Node/Static choice rides on their own row, as two icons at
 * its end, by input id to the choosing parameter: every discriminated input
 * of a source-control face that does not place the choice in a section of its
 * own, and does not follow its wire (the Condition's B has no choice to make).
 * The Parameters section leaves these out.
 */
export function inlineInputModes(schema: UniversalNodeSchema): Record<string, string> {
  const face = resolveFace(schema)
  const placed = new Set(
    (face.sections ?? []).flatMap((section) => [
      ...(section.params ?? []),
      ...sectionRows(section).flatMap((row) => row.params),
    ]),
  )
  const modes: Record<string, string> = {}
  for (const input of schema.inputs) {
    const parameter = input.discriminatedUnion?.parameterName
    if (!parameter || placed.has(parameter) || input.discriminatedUnion?.followsWire) continue
    if (inputControl(face, input) !== 'source') continue
    modes[input.id] = parameter
  }
  return modes
}

/** A section's paired rows: its own, then its toggle groups'. */
export function sectionRows(section: FaceSection): FaceRow[] {
  return [...(section.rows ?? []), ...(section.groups ?? []).flatMap((group) => group.rows)]
}

/**
 * The formula a node draws on itself (`face.equation`), or null when it has
 * none. Callers pass what the formula reads: its settings, its inputs as its
 * own rows show them, how many are wired.
 */
export function nodeEquation(
  schema: UniversalNodeSchema,
  context: Partial<SummaryContext>,
): string | null {
  const equation = resolveFace(schema).equation
  if (!equation) return null
  return equation({
    params: {},
    input: (handle) => handle,
    staticValue: () => undefined,
    wiredInputs: 0,
    consumerField: 'close',
    ...context,
  })
}
